import { ethers } from "ethers";
import { gql } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Token as UniToken } from "@uniswap/sdk-core";
import { Pool } from "@uniswap/v3-sdk";
import useSWR from "swr";

import OrderBook from "abis/OrderBook.json";
import PositionManager from "abis/PositionManager.json";
import Vault from "abis/Vault.json";
import Router from "abis/Router.json";
import UniPool from "abis/UniPool.json";
import UniswapV2 from "abis/UniswapV2.json";
import Token from "abis/Token.json";
import PositionRouter from "abis/PositionRouter.json";

import { getContract } from "config/contracts";
import { ARBITRUM, ARBITRUM_TESTNET, AVALANCHE, getConstant, getHighExecutionFee } from "config/chains";
import { DECREASE, getOrderKey, INCREASE, SWAP, USD_DECIMALS } from "lib/legacy";

import { groupBy } from "lodash";
import { UI_VERSION } from "config/ui";
import { getServerBaseUrl, getServerUrl } from "config/backend";
import { getGmxGraphClient, nissohGraphClient } from "lib/subgraph/clients";
import { callContract, contractFetcher } from "lib/contracts";
import { replaceNativeTokenAddress } from "./tokens";
import { getUsd } from "./tokens/utils";
import { getProvider } from "lib/rpc";
import { bigNumberify, expandDecimals, parseValue } from "lib/numbers";
import { getTokenBySymbol } from "config/tokens";
import { t } from "@lingui/macro";

export * from "./prices";

const { AddressZero } = ethers.constants;

export function useAllOrdersStats(chainId) {
  const query = gql(`{
    orderStat(id: "total") {
      openSwap
      openIncrease
      openDecrease
      executedSwap
      executedIncrease
      executedDecrease
      cancelledSwap
      cancelledIncrease
      cancelledDecrease
    }
  }`);

  const [res, setRes] = useState();

  useEffect(() => {
    const graphClient = getGmxGraphClient(chainId);
    if (graphClient) {
      graphClient.query({ query }).then(setRes).catch(console.warn);
    }
  }, [setRes, query, chainId]);

  return res ? res.data.orderStat : null;
}

export function useUserStat(chainId) {
  const query = gql(`{
    userStat(id: "total") {
      id
      uniqueCount
    }
  }`);

  const [res, setRes] = useState();

  useEffect(() => {
    getGmxGraphClient(chainId).query({ query }).then(setRes).catch(console.warn);
  }, [setRes, query, chainId]);

  return res ? res.data.userStat : null;
}

export function useLiquidationsData(chainId, account) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (account) {
      const query = gql(`{
         liquidatedPositions(
           where: {account: "${account.toLowerCase()}"}
           first: 100
           orderBy: timestamp
           orderDirection: desc
         ) {
           key
           timestamp
           borrowFee
           loss
           collateral
           size
           markPrice
           type
         }
      }`);
      const graphClient = getGmxGraphClient(chainId);
      if (!graphClient) {
        return;
      }

      graphClient
        .query({ query })
        .then((res) => {
          const _data = res.data.liquidatedPositions.map((item) => {
            return {
              ...item,
              size: bigNumberify(item.size),
              collateral: bigNumberify(item.collateral),
              markPrice: bigNumberify(item.markPrice),
            };
          });
          setData(_data);
        })
        .catch(console.warn);
    }
  }, [setData, chainId, account]);

  return data;
}

export function useAllPositions(chainId, library) {
  const count = 1000;
  const query = gql(`{
    aggregatedTradeOpens(
      first: ${count}
    ) {
      account
      initialPosition{
        indexToken
        collateralToken
        isLong
        sizeDelta
      }
      increaseList {
        sizeDelta
      }
      decreaseList {
        sizeDelta
      }
    }
  }`);

  const [res, setRes] = useState();

  useEffect(() => {
    nissohGraphClient.query({ query }).then(setRes).catch(console.warn);
  }, [setRes, query]);

  const key = res ? `allPositions${count}__` : false;
  const { data: positions = [] } = useSWR(key, async () => {
    const provider = getProvider(library, chainId);
    const vaultAddress = getContract(chainId, "Vault");
    const contract = new ethers.Contract(vaultAddress, Vault.abi, provider);
    const ret = await Promise.all(
      res.data.aggregatedTradeOpens.map(async (dataItem) => {
        try {
          const { indexToken, collateralToken, isLong } = dataItem.initialPosition;
          const positionData = await contract.getPosition(dataItem.account, collateralToken, indexToken, isLong);
          const position = {
            size: bigNumberify(positionData[0]),
            collateral: bigNumberify(positionData[1]),
            entryFundingRate: bigNumberify(positionData[3]),
            account: dataItem.account,
          };
          position.fundingFee = await contract.getFundingFee(collateralToken, position.size, position.entryFundingRate);
          position.marginFee = position.size.div(1000);
          position.fee = position.fundingFee.add(position.marginFee);

          const THRESHOLD = 5000;
          const collateralDiffPercent = position.fee.mul(10000).div(position.collateral);
          position.danger = collateralDiffPercent.gt(THRESHOLD);

          return position;
        } catch (ex) {
          console.error(ex);
        }
      })
    );

    return ret.filter(Boolean);
  });

  return positions;
}

export function useAllOrders(chainId, library) {
  const query = gql(`{
    orders(
      first: 1000,
      orderBy: createdTimestamp,
      orderDirection: desc,
      where: {status: "open"}
    ) {
      type
      account
      index
      status
      createdTimestamp
    }
  }`);

  const [res, setRes] = useState();

  useEffect(() => {
    getGmxGraphClient(chainId).query({ query }).then(setRes);
  }, [setRes, query, chainId]);

  const key = res ? res.data.orders.map((order) => `${order.type}-${order.account}-${order.index}`) : null;
  const { data: orders = [] } = useSWR(key, () => {
    const provider = getProvider(library, chainId);
    const orderBookAddress = getContract(chainId, "OrderBook");
    const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, provider);
    return Promise.all(
      res.data.orders.map(async (order) => {
        try {
          const type = order.type.charAt(0).toUpperCase() + order.type.substring(1);
          const method = `get${type}Order`;
          const orderFromChain = await contract[method](order.account, order.index);
          const ret = {};
          for (const [key, val] of Object.entries(orderFromChain)) {
            ret[key] = val;
          }
          if (order.type === "swap") {
            ret.path = [ret.path0, ret.path1, ret.path2].filter((address) => address !== AddressZero);
          }
          ret.type = type;
          ret.index = order.index;
          ret.account = order.account;
          ret.createdTimestamp = order.createdTimestamp;
          return ret;
        } catch (ex) {
          console.error(ex);
        }
      })
    );
  });

  return orders.filter(Boolean);
}

export function usePositionsForOrders(chainId, library, orders) {
  const key = orders ? orders.map((order) => getOrderKey(order) + "____") : null;
  const { data: positions = {} } = useSWR(key, async () => {
    const provider = getProvider(library, chainId);
    const vaultAddress = getContract(chainId, "Vault");
    const contract = new ethers.Contract(vaultAddress, Vault.abi, provider);
    const data = await Promise.all(
      orders.map(async (order) => {
        try {
          const position = await contract.getPosition(
            order.account,
            order.collateralToken,
            order.indexToken,
            order.isLong
          );
          if (position[0].eq(0)) {
            return [null, order];
          }
          return [position, order];
        } catch (ex) {
          console.error(ex);
        }
      })
    );
    return data.reduce((memo, [position, order]) => {
      memo[getOrderKey(order)] = position;
      return memo;
    }, {});
  });

  return positions;
}

function invariant(condition, errorMsg) {
  if (!condition) {
    throw new Error(errorMsg);
  }
}
// const dummyResponse = `{"trades":[{"event":"MarketExecuted","timestamp":"1679906183","orderId":"1002831","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":1,"index":0,"initialPosToken":227065000000000000000,"positionSizeDai":227065000000000000000,"openPrice":17539337070656,"buy":true,"leverage":66,"tp":19385300000000,"sl":0,"open":false,"price":17535200000000,"priceImpactP":0,"percentProfit":-15567672951,"daiSentToTrader":202699183241381180000},{"event":"MarketExecuted","timestamp":"1679906666","orderId":"1002833","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":1,"index":0,"initialPosToken":227065000000000000000,"positionSizeDai":227065000000000000000,"openPrice":17539881240000,"buy":false,"leverage":66,"tp":16739800000000,"sl":0,"open":false,"price":17537800000000,"priceImpactP":0,"percentProfit":7831400801,"daiSentToTrader":208012293922879080000},{"event":"MarketExecuted","timestamp":"1679907044","orderId":"1002836","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":0,"index":0,"initialPosToken":86100000000000000000,"positionSizeDai":86100000000000000000,"openPrice":278137950300000,"buy":false,"leverage":100,"tp":277795800000000,"sl":0,"open":false,"price":278323450000000,"priceImpactP":0,"percentProfit":-66693415911,"daiSentToTrader":68389796890062900000},{"event":"MarketExecuted","timestamp":"1679907146","orderId":"1002838","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":1,"index":1,"initialPosToken":86100000000000000000,"positionSizeDai":86100000000000000000,"openPrice":17578828720000,"buy":true,"leverage":100,"tp":17604400000000,"sl":0,"open":false,"price":17573115776000,"priceImpactP":0,"percentProfit":-32499002584,"daiSentToTrader":71333935877517600000},{"event":"MarketExecuted","timestamp":"1679907233","orderId":"1002839","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":1,"index":0,"initialPosToken":227065000000000000000,"positionSizeDai":227065000000000000000,"openPrice":17537582160000,"buy":false,"leverage":66,"tp":16739800000000,"sl":0,"open":false,"price":17586100000000,"priceImpactP":0,"percentProfit":-182589447666,"daiSentToTrader":164774383965719720000},{"event":"MarketExecuted","timestamp":"1679907608","orderId":"1002841","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":1,"index":0,"initialPosToken":93050000000000000000,"positionSizeDai":93050000000000000000,"openPrice":17558473800000,"buy":false,"leverage":50,"tp":17474500000000,"sl":0,"open":false,"price":17562000000000,"priceImpactP":0,"percentProfit":-10041305526,"daiSentToTrader":85648681520805700000},{"event":"MarketExecuted","timestamp":"1679908262","orderId":"1002843","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":1,"index":0,"initialPosToken":93050000000000000000,"positionSizeDai":93050000000000000000,"openPrice":17546778480000,"buy":false,"leverage":50,"tp":17468500000000,"sl":0,"open":false,"price":17554400000000,"priceImpactP":0,"percentProfit":-21717718750,"daiSentToTrader":84562191270312500000},{"event":"MarketExecuted","timestamp":"1679908886","orderId":"1002845","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":1,"index":0,"initialPosToken":93050000000000000000,"positionSizeDai":93050000000000000000,"openPrice":17546678520000,"buy":false,"leverage":50,"tp":17468500000000,"sl":0,"open":false,"price":17642200000000,"priceImpactP":0,"percentProfit":-272192483298,"daiSentToTrader":61255514429121100000},{"event":"MarketExecuted","timestamp":"1679909516","orderId":"1002849","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":1,"index":0,"initialPosToken":93050000000000000000,"positionSizeDai":93050000000000000000,"openPrice":17608253880000,"buy":false,"leverage":50,"tp":17524100000000,"sl":0,"open":false,"price":17607200000000,"priceImpactP":0,"percentProfit":2992573844,"daiSentToTrader":86861483996184200000},{"event":"MarketExecuted","timestamp":"1679909792","orderId":"1002851","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":1,"index":0,"initialPosToken":93050000000000000000,"positionSizeDai":93050000000000000000,"openPrice":17596958400000,"buy":false,"leverage":50,"tp":17524100000000,"sl":0,"open":false,"price":17602700000000,"priceImpactP":0,"percentProfit":-16314183023,"daiSentToTrader":85064990269709840000},{"event":"MarketExecuted","timestamp":"1679910782","orderId":"1002853","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":1,"index":0,"initialPosToken":93050000000000000000,"positionSizeDai":93050000000000000000,"openPrice":17615451000000,"buy":false,"leverage":50,"tp":17540900000000,"sl":0,"open":false,"price":17634100000000,"priceImpactP":0,"percentProfit":-52933643311,"daiSentToTrader":81657549489911450000},{"event":"MarketExecuted","timestamp":"1679912294","orderId":"1002855","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":1,"index":0,"initialPosToken":93050000000000000000,"positionSizeDai":93050000000000000000,"openPrice":17625233185560,"buy":false,"leverage":50,"tp":17540700000000,"sl":0,"open":false,"price":17626741003000,"priceImpactP":0,"percentProfit":-4277439691,"daiSentToTrader":86185009236752450000},{"event":"MarketExecuted","timestamp":"1679912483","orderId":"1002857","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":1,"index":0,"initialPosToken":93050000000000000000,"positionSizeDai":93050000000000000000,"openPrice":17609153520000,"buy":false,"leverage":50,"tp":17532000000000,"sl":0,"open":false,"price":17619900000000,"priceImpactP":0,"percentProfit":-30513902862,"daiSentToTrader":83743706338690900000},{"event":"MarketExecuted","timestamp":"1679913848","orderId":"1002860","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":1,"index":0,"initialPosToken":93050000000000000000,"positionSizeDai":93050000000000000000,"openPrice":17623747680000,"buy":false,"leverage":50,"tp":17542700000000,"sl":0,"open":false,"price":17635400000000,"priceImpactP":0,"percentProfit":-33058575881,"daiSentToTrader":83506924514272950000},{"event":"MarketExecuted","timestamp":"1679914142","orderId":"1002862","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":1,"index":0,"initialPosToken":93050000000000000000,"positionSizeDai":93050000000000000000,"openPrice":17635942800000,"buy":false,"leverage":50,"tp":17556099999999,"sl":0,"open":false,"price":17654600000000,"priceImpactP":0,"percentProfit":-52895385893,"daiSentToTrader":81661109342656350000},{"event":"MarketExecuted","timestamp":"1679915402","orderId":"1002867","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":47,"index":0,"initialPosToken":79150000000000000000,"positionSizeDai":79150000000000000000,"openPrice":3281077971025,"buy":true,"leverage":150,"tp":3434110000000,"sl":3278809999999,"open":false,"price":3282000000000,"priceImpactP":0,"percentProfit":42152105945,"daiSentToTrader":65983564185546750000},{"event":"LimitExecutedEvent","timestamp":"1679917796","orderId":"1002883","limitIndex":"0","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":47,"index":0,"initialPosToken":79150000000000000000,"positionSizeDai":79150000000000000000,"openPrice":3284078042319,"buy":true,"leverage":150,"tp":3436210000000,"sl":3280810000000,"nftHolder":"0xB707AAC3b1F1D5a93cCAf0bCe1328aE9c2F14ed9","orderType":1,"price":3280810000000,"priceImpactP":0,"percentProfit":-149267569629,"daiSentToTrader":50832696863864650000},{"event":"LimitExecutedEvent","timestamp":"1679918174","orderId":"1002888","limitIndex":"0","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":104,"index":0,"initialPosToken":79150000000000000000,"positionSizeDai":79150000000000000000,"openPrice":19287954152,"buy":false,"leverage":150,"tp":18403100000,"sl":19316900000,"nftHolder":"0xB707AAC3b1F1D5a93cCAf0bCe1328aE9c2F14ed9","orderType":1,"price":19316900000,"priceImpactP":0,"percentProfit":-225108228990,"daiSentToTrader":44829908675441500000},{"event":"MarketExecuted","timestamp":"1679918237","orderId":"1002889","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":1,"index":0,"initialPosToken":93050000000000000000,"positionSizeDai":93050000000000000000,"openPrice":17631444600000,"buy":false,"leverage":50,"tp":17540200000000,"sl":0,"open":false,"price":17665154664000,"priceImpactP":0,"percentProfit":-95596432296,"daiSentToTrader":77687776974857200000},{"event":"LimitExecutedEvent","timestamp":"1679921178","orderId":"1002903","limitIndex":"0","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":5,"index":0,"initialPosToken":79150000000000000000,"positionSizeDai":79150000000000000000,"openPrice":3543000000,"buy":true,"leverage":150,"tp":3708300000,"sl":3540600000,"nftHolder":"0xBc10595E70A2B819865774ce43D3Ef7EE8D172AC","orderType":1,"price":3540600000,"priceImpactP":0,"percentProfit":-101608806096,"daiSentToTrader":54604887997501600000},{"event":"LimitExecutedEvent","timestamp":"1679926659","orderId":"1002930","limitIndex":"0","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":47,"index":0,"initialPosToken":93050000000000000000,"positionSizeDai":93050000000000000000,"openPrice":3276000000000,"buy":true,"leverage":50,"tp":3734640000000,"sl":3269450000000,"nftHolder":"0xBc10595E70A2B819865774ce43D3Ef7EE8D172AC","orderType":1,"price":3269450000000,"priceImpactP":0,"percentProfit":-99969474969,"daiSentToTrader":77280865354134550000},{"event":"MarketExecuted","timestamp":"1679927223","orderId":"1002939","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":47,"index":0,"initialPosToken":93050000000000000000,"positionSizeDai":93050000000000000000,"openPrice":3261000000000,"buy":true,"leverage":50,"tp":3717540000000,"sl":0,"open":false,"price":3257000000000,"priceImpactP":0,"percentProfit":-61330880098,"daiSentToTrader":80876186606881100000},{"event":"LimitExecutedEvent","timestamp":"1679928423","orderId":"1002956","limitIndex":"0","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":47,"index":0,"initialPosToken":93050000000000000000,"positionSizeDai":93050000000000000000,"openPrice":3261000000000,"buy":false,"leverage":50,"tp":3244700000000,"sl":3267520000000,"nftHolder":"0xB707AAC3b1F1D5a93cCAf0bCe1328aE9c2F14ed9","orderType":1,"price":3267520000000,"priceImpactP":0,"percentProfit":-99969334559,"daiSentToTrader":77280878419285050000},{"event":"LimitExecutedEvent","timestamp":"1679929095","orderId":"1002965","limitIndex":"0","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":47,"index":0,"initialPosToken":93050000000000000000,"positionSizeDai":93050000000000000000,"openPrice":3264000000000,"buy":true,"leverage":50,"tp":3280320000000,"sl":3257470000000,"nftHolder":"0xB707AAC3b1F1D5a93cCAf0bCe1328aE9c2F14ed9","orderType":1,"price":3257470000000,"priceImpactP":0,"percentProfit":-100030637254,"daiSentToTrader":77275174203515300000},{"event":"LimitExecutedEvent","timestamp":"1679929533","orderId":"1002990","limitIndex":"0","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":5,"index":0,"initialPosToken":93050000000000000000,"positionSizeDai":93050000000000000000,"openPrice":3508000000,"buy":true,"leverage":50,"tp":3525499999,"sl":0,"nftHolder":"0xBc10595E70A2B819865774ce43D3Ef7EE8D172AC","orderType":2,"price":3395000000,"priceImpactP":0,"percentProfit":-1610604332953,"daiSentToTrader":0},{"event":"LimitExecutedEvent","timestamp":"1679930061","orderId":"1003014","limitIndex":"0","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":52,"index":0,"initialPosToken":93050000000000000000,"positionSizeDai":93050000000000000000,"openPrice":3670000000,"buy":false,"leverage":50,"tp":3651700000,"sl":3677300000,"nftHolder":"0xBc10595E70A2B819865774ce43D3Ef7EE8D172AC","orderType":0,"price":3651700000,"priceImpactP":0,"percentProfit":249318801089,"daiSentToTrader":109782139441331450000},{"event":"MarketExecuted","timestamp":"1679930229","orderId":"1003022","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":0,"index":0,"initialPosToken":93050000000000000000,"positionSizeDai":93050000000000000000,"openPrice":267093000000000,"buy":true,"leverage":50,"tp":268428500000000,"sl":266558800000000,"open":false,"price":267816000000000,"priceImpactP":0,"percentProfit":135346115398,"daiSentToTrader":99176981037783900000},{"event":"LimitExecutedEvent","timestamp":"1679930292","orderId":"1003023","limitIndex":"0","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":4,"index":0,"initialPosToken":93050000000000000000,"positionSizeDai":93050000000000000000,"openPrice":10390000000,"buy":true,"leverage":50,"tp":10441900000,"sl":10369200000,"nftHolder":"0xB707AAC3b1F1D5a93cCAf0bCe1328aE9c2F14ed9","orderType":0,"price":10441900000,"priceImpactP":0,"percentProfit":249759384023,"daiSentToTrader":109823135683340160000},{"event":"MarketExecuted","timestamp":"1679930439","orderId":"1003025","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":19,"index":0,"initialPosToken":93050000000000000000,"positionSizeDai":93050000000000000000,"openPrice":4602900000,"buy":false,"leverage":50,"tp":4579900000,"sl":4612100000,"open":false,"price":4617100000,"priceImpactP":0,"percentProfit":-154250581155,"daiSentToTrader":72230008423527250000},{"event":"MarketExecuted","timestamp":"1679930667","orderId":"1003029","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":91,"index":0,"initialPosToken":823492500000000000000,"positionSizeDai":823492500000000000000,"openPrice":231677634000,"buy":true,"leverage":50,"tp":263887000000,"sl":0,"open":false,"price":230260000000,"priceImpactP":0,"percentProfit":-305949688695,"daiSentToTrader":514312497232332700000},{"event":"MarketExecuted","timestamp":"1679933106","orderId":"1003043","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":19,"index":0,"initialPosToken":93050000000000000000,"positionSizeDai":93050000000000000000,"openPrice":4587500000,"buy":true,"leverage":50,"tp":4610400000,"sl":4578300000,"open":false,"price":4621000000,"priceImpactP":0,"percentProfit":365122615803,"daiSentToTrader":120557684400469150000},{"event":"LimitExecutedEvent","timestamp":"1679934135","orderId":"1003058","limitIndex":"0","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":11,"index":0,"initialPosToken":93050000000000000000,"positionSizeDai":93050000000000000000,"openPrice":58940000000,"buy":true,"leverage":50,"tp":59234700000,"sl":58822100000,"nftHolder":"0xBc10595E70A2B819865774ce43D3Ef7EE8D172AC","orderType":1,"price":58822100000,"priceImpactP":0,"percentProfit":-100016966406,"daiSentToTrader":77276446275921700000},{"event":"MarketExecuted","timestamp":"1679935311","orderId":"1003071","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":0,"index":0,"initialPosToken":93050000000000000000,"positionSizeDai":93050000000000000000,"openPrice":272563000000000,"buy":true,"leverage":50,"tp":273925800000000,"sl":0,"open":false,"price":271522350000000,"priceImpactP":0,"percentProfit":-190900819260,"daiSentToTrader":68819703767857000000},{"event":"LimitExecutedEvent","timestamp":"1679935311","orderId":"1003070","limitIndex":"0","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":73,"index":0,"initialPosToken":91660000000000000000,"positionSizeDai":91660000000000000000,"openPrice":958691000000,"buy":true,"leverage":30,"tp":966680000000,"sl":955495000000,"nftHolder":"0xB707AAC3b1F1D5a93cCAf0bCe1328aE9c2F14ed9","orderType":1,"price":955150000000,"priceImpactP":0,"percentProfit":-110807340425,"daiSentToTrader":73858955176644500000},{"event":"LimitExecutedEvent","timestamp":"1679937915","orderId":"1003090","limitIndex":"1","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":73,"index":1,"initialPosToken":91660000000000000000,"positionSizeDai":91660000000000000000,"openPrice":958700000000,"buy":false,"leverage":30,"tp":950711000000,"sl":961896000000,"nftHolder":"0xB707AAC3b1F1D5a93cCAf0bCe1328aE9c2F14ed9","orderType":1,"price":962400000000,"priceImpactP":0,"percentProfit":-115781787837,"daiSentToTrader":73402997326860580000},{"event":"LimitExecutedEvent","timestamp":"1679990184","orderId":"1003186","limitIndex":"0","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":0,"index":0,"initialPosToken":111749416324804690000,"positionSizeDai":114451500000000000000,"openPrice":270417123600000,"buy":true,"leverage":50,"tp":271846500000000,"sl":269953000000000,"nftHolder":"0xBc10595E70A2B819865774ce43D3Ef7EE8D172AC","orderType":1,"price":269953000000000,"priceImpactP":0,"percentProfit":-85816237119,"daiSentToTrader":94392917480991470000},{"event":"LimitExecutedEvent","timestamp":"1679993121","orderId":"1003198","limitIndex":"0","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":0,"index":0,"initialPosToken":274966308852234450000,"positionSizeDai":309856500000000000000,"openPrice":269447527860000,"buy":false,"leverage":50,"tp":267500,"sl":270000000000000,"nftHolder":"0xBc10595E70A2B819865774ce43D3Ef7EE8D172AC","orderType":1,"price":270000000000000,"priceImpactP":0,"percentProfit":-102519430107,"daiSentToTrader":227666761104847730000},{"event":"MarketExecuted","timestamp":"1679993901","orderId":"1003200","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":0,"index":0,"initialPosToken":93050000000000000000,"positionSizeDai":93050000000000000000,"openPrice":270398000000000,"buy":true,"leverage":50,"tp":271750000000000,"sl":269857200000000,"open":false,"price":270101550000000,"priceImpactP":0,"percentProfit":-54817343323,"daiSentToTrader":81482271203794850000},{"event":"LimitExecutedEvent","timestamp":"1679995287","orderId":"1003204","limitIndex":"0","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":0,"index":0,"initialPosToken":93050000000000000000,"positionSizeDai":93050000000000000000,"openPrice":270209000000000,"buy":true,"leverage":50,"tp":271560000000000,"sl":269668600000000,"nftHolder":"0xB707AAC3b1F1D5a93cCAf0bCe1328aE9c2F14ed9","orderType":1,"price":269668600000000,"priceImpactP":0,"percentProfit":-99996669244,"daiSentToTrader":77278334926845800000},{"event":"LimitExecutedEvent","timestamp":"1679996841","orderId":"1003205","limitIndex":"0","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":1,"index":0,"initialPosToken":93050000000000000000,"positionSizeDai":93050000000000000000,"openPrice":17284800000000,"buy":false,"leverage":50,"tp":17198400000000,"sl":17319400000000,"nftHolder":"0xBc10595E70A2B819865774ce43D3Ef7EE8D172AC","orderType":0,"price":17198400000000,"priceImpactP":0,"percentProfit":249930574840,"daiSentToTrader":109839064988862000000},{"event":"LimitExecutedEvent","timestamp":"1680005199","orderId":"1003230","limitIndex":"0","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":0,"index":0,"initialPosToken":93050000000000000000,"positionSizeDai":93050000000000000000,"openPrice":270274000000000,"buy":false,"leverage":50,"tp":268922599999999,"sl":270814500000000,"nftHolder":"0xB707AAC3b1F1D5a93cCAf0bCe1328aE9c2F14ed9","orderType":1,"price":270814500000000,"priceImpactP":0,"percentProfit":-99991120122,"daiSentToTrader":77278851272647900000},{"event":"LimitExecutedEvent","timestamp":"1680011607","orderId":"1003259","limitIndex":"0","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":1,"index":0,"initialPosToken":114451500000000000000,"positionSizeDai":114451500000000000000,"openPrice":17000000000000,"buy":false,"leverage":50,"tp":14620000000000,"sl":0,"nftHolder":"0xB707AAC3b1F1D5a93cCAf0bCe1328aE9c2F14ed9","orderType":2,"price":17432300000000,"priceImpactP":0,"percentProfit":-1271470588235,"daiSentToTrader":0},{"event":"MarketExecuted","timestamp":"1680018995","orderId":"1003280","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":1,"index":0,"initialPosToken":122682500000000000000,"positionSizeDai":122682500000000000000,"openPrice":17392512853943,"buy":false,"leverage":150,"tp":16609600000000,"sl":0,"open":false,"price":17410660751000,"priceImpactP":0,"percentProfit":-156514736048,"daiSentToTrader":77901579644791240000},{"event":"MarketExecuted","timestamp":"1680019043","orderId":"1003281","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":0,"index":0,"initialPosToken":122682500000000000000,"positionSizeDai":122682500000000000000,"openPrice":269919924800000,"buy":true,"leverage":150,"tp":282064100000000,"sl":0,"open":false,"price":269759800000000,"priceImpactP":0,"percentProfit":-88984612817,"daiSentToTrader":86186343988078400000},{"event":"LimitExecutedEvent","timestamp":"1680019169","orderId":"1003283","limitIndex":"0","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":38,"index":0,"initialPosToken":26911000000000000000,"positionSizeDai":26911000000000000000,"openPrice":506200000000,"buy":false,"leverage":150,"tp":496076000000,"sl":507887000000,"nftHolder":"0xBc10595E70A2B819865774ce43D3Ef7EE8D172AC","orderType":1,"price":507887000000,"priceImpactP":0,"percentProfit":-499901224812,"daiSentToTrader":7847214639084268000},{"event":"MarketExecuted","timestamp":"1680077875","orderId":"1003347","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":0,"index":0,"initialPosToken":139575000000000000000,"positionSizeDai":139575000000000000000,"openPrice":282788070000000,"buy":true,"leverage":50,"tp":322378399800000,"sl":0,"open":false,"price":283500050000000,"priceImpactP":0,"percentProfit":125885791433,"daiSentToTrader":147445046839260970000},{"event":"LimitExecutedEvent","timestamp":"1680197473","orderId":"1003624","limitIndex":"0","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":0,"index":0,"initialPosToken":139575000000000000000,"positionSizeDai":139575000000000000000,"openPrice":285545000000000,"buy":true,"leverage":50,"tp":325521300000000,"sl":0,"nftHolder":"0xBc10595E70A2B819865774ce43D3Ef7EE8D172AC","orderType":2,"price":279055000000000,"priceImpactP":0,"percentProfit":-1136423330823,"daiSentToTrader":0},{"event":"MarketExecuted","timestamp":"1680247133","orderId":"1003722","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":0,"index":0,"initialPosToken":118725000000000000000,"positionSizeDai":118725000000000000000,"openPrice":278951124900000,"buy":false,"leverage":150,"tp":278659800000000,"sl":279311100000000,"open":false,"price":279149000000000,"priceImpactP":0,"percentProfit":-106403102015,"daiSentToTrader":81338129213269130000},{"event":"LimitExecutedEvent","timestamp":"1680247301","orderId":"1003728","limitIndex":"0","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":23,"index":0,"initialPosToken":134362500000000000000,"positionSizeDai":134362500000000000000,"openPrice":12380537930,"buy":true,"leverage":500,"tp":12386200000,"sl":12377500000,"nftHolder":"0xB707AAC3b1F1D5a93cCAf0bCe1328aE9c2F14ed9","orderType":1,"price":12370000000,"priceImpactP":0,"percentProfit":-425584496391,"daiSentToTrader":63172612478664260000},{"event":"LimitExecutedEvent","timestamp":"1680247400","orderId":"1003729","limitIndex":"0","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":0,"index":0,"initialPosToken":118725000000000000000,"positionSizeDai":118725000000000000000,"openPrice":278975000000000,"buy":true,"leverage":150,"tp":279440000000000,"sl":278789000000000,"nftHolder":"0xBc10595E70A2B819865774ce43D3Ef7EE8D172AC","orderType":1,"price":278789000000000,"priceImpactP":0,"percentProfit":-100008961376,"daiSentToTrader":82097273560634400000},{"event":"MarketExecuted","timestamp":"1680262037","orderId":"1003734","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":57,"index":0,"initialPosToken":139575000000000000000,"positionSizeDai":139575000000000000000,"openPrice":105392,"buy":false,"leverage":50,"tp":90644,"sl":0,"open":false,"price":100000,"priceImpactP":0,"percentProfit":2558068923637,"daiSentToTrader":486917007516634250000},{"event":"MarketExecuted","timestamp":"1680262085","orderId":"1003735","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":23,"index":0,"initialPosToken":142744200000000000000,"positionSizeDai":142744200000000000000,"openPrice":12371237000,"buy":true,"leverage":232,"tp":12744507081,"sl":0,"open":false,"price":12370000000,"priceImpactP":0,"percentProfit":-23197680231,"daiSentToTrader":132528043251170080000},{"event":"MarketExecuted","timestamp":"1680262274","orderId":"1003736","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":7,"index":0,"initialPosToken":232625000000000000000,"positionSizeDai":232625000000000000000,"openPrice":717181363978,"buy":true,"leverage":50,"tp":817380000000,"sl":0,"open":false,"price":710000000000,"priceImpactP":0,"percentProfit":-500665824483,"daiSentToTrader":99990175079642120000},{"event":"MarketExecuted","timestamp":"1680277331","orderId":"1003771","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":0,"index":0,"initialPosToken":139575000000000000000,"positionSizeDai":139575000000000000000,"openPrice":283773413980000,"buy":true,"leverage":50,"tp":285257200000000,"sl":283270300000000,"open":false,"price":283774850000000,"priceImpactP":0,"percentProfit":253022293,"daiSentToTrader":129909853086545480000},{"event":"MarketExecuted","timestamp":"1680278171","orderId":"1003778","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":0,"index":0,"initialPosToken":237277500000000000000,"positionSizeDai":237277500000000000000,"openPrice":284307781380000,"buy":false,"leverage":50,"tp":283011800000000,"sl":0,"open":false,"price":284731450000000,"priceImpactP":0,"percentProfit":-74508797814,"daiSentToTrader":203107452476688630000},{"event":"LimitExecutedEvent","timestamp":"1680283211","orderId":"1003819","limitIndex":"0","trader":"0xbF13755c11F362E59B3df8BCadA06CAf1C2cdB99","pairIndex":91,"index":0,"initialPosToken":93050000000000000000,"positionSizeDai":93050000000000000000,"openPrice":240656224000,"buy":true,"leverage":50,"tp":254972000000,"sl":240059000000,"nftHolder":"0xB707AAC3b1F1D5a93cCAf0bCe1328aE9c2F14ed9","orderType":1,"price":239995000000,"priceImpactP":0,"percentProfit":-137379368172,"daiSentToTrader":73799874791595400000}]}`;
export function useTrades(chainId, account, forSingleAccount, afterId) {
  //let baseUrl = "https://dexynth-historial-api.herokuapp.com";
  let baseUrl = "https://dexynth-historial-api.herokuapp.com";
  let date = "2023-03-27";
  let url = account && `${baseUrl}/data/${date}/${account}`;
  //console.log("url", url);

  /*
  let url =
    account && account.length > 0
      ? `${getServerBaseUrl(chainId)}/actions?account=${account}`
      : !forSingleAccount && `${getServerBaseUrl(chainId)}/actions`;

  if (afterId && afterId.length > 0) {
    const urlItem = new URL(url);
    urlItem.searchParams.append("after", afterId);
    url = urlItem.toString();
  }*/

  const { data: _trades, mutate: updateTrades } = useSWR(url && url, {
    dedupingInterval: 10000,

    fetcher: (...args) => fetch(...args).then((res) => res.json()),
  });
  //const updateTrades = () => {};
  //const trades = JSON.parse(dummyResponse).trades;
  //console.log(">>trades", _trades);
  const trades = _trades?.trades;
  if (trades) {
    trades.sort((item0, item1) => {
      //const data0 = item0.data;
      //const data1 = item1.data;
      const time0 = parseInt(item0.timestamp);
      const time1 = parseInt(item1.timestamp);
      if (time1 > time0) {
        return 1;
      }
      if (time1 < time0) {
        return -1;
      }
      /*
      const block0 = parseInt(item0.blockNumber);
      const block1 = parseInt(item1.blockNumber);

      if (isNaN(block0) && isNaN(block1)) {
        return 0;
      }

      if (isNaN(block0)) {
        return 1;
      }

      if (isNaN(block1)) {
        return -1;
      }

      if (block1 > block0) {
        return 1;
      }

      if (block1 < block0) {
        return -1;
      }*/

      return 0;
    });
  }
  //let _trades = trades?.trades;
  return { trades, updateTrades };
}

export function useMinExecutionFee(library, active, chainId, infoTokens) {
  const positionRouterAddress = getContract(chainId, "PositionRouter");
  const nativeTokenAddress = getContract(chainId, "NATIVE_TOKEN");

  const { data: minExecutionFee } = useSWR([active, chainId, positionRouterAddress, "minExecutionFee"], {
    fetcher: contractFetcher(library, PositionRouter),
  });

  const { data: gasPrice } = useSWR(["gasPrice", chainId], {
    fetcher: () => {
      return new Promise(async (resolve, reject) => {
        const provider = getProvider(library, chainId);
        if (!provider) {
          resolve(undefined);
          return;
        }

        try {
          const gasPrice = await provider.getGasPrice();
          resolve(gasPrice);
        } catch (e) {
          console.error(e);
        }
      });
    },
  });

  let multiplier;

  // if gas prices on Arbitrum are high, the main transaction costs would come from the L2 gas usage
  // for executing positions this is around 65,000 gas
  // if gas prices on Ethereum are high, than the gas usage might be higher, this calculation doesn't deal with that
  // case yet
  if (chainId === ARBITRUM || chainId === ARBITRUM_TESTNET) {
    multiplier = 65000;
  }

  // multiplier for Avalanche is just the average gas usage
  if (chainId === AVALANCHE) {
    multiplier = 700000;
  }

  let finalExecutionFee = minExecutionFee;

  if (gasPrice && minExecutionFee) {
    const estimatedExecutionFee = gasPrice.mul(multiplier);
    if (estimatedExecutionFee.gt(minExecutionFee)) {
      finalExecutionFee = estimatedExecutionFee;
    }
  }

  const finalExecutionFeeUSD = getUsd(finalExecutionFee, nativeTokenAddress, false, infoTokens);
  const isFeeHigh = finalExecutionFeeUSD?.gt(expandDecimals(getHighExecutionFee(chainId), USD_DECIMALS));
  const errorMessage =
    isFeeHigh &&
    `The network cost to send transactions is high at the moment, please check the "Execution Fee" value before proceeding.`;

  return {
    minExecutionFee: finalExecutionFee,
    minExecutionFeeUSD: finalExecutionFeeUSD,
    minExecutionFeeErrorMessage: errorMessage,
  };
}

export function useStakedGmxSupply(library, active) {
  const gmxAddressArb = getContract(ARBITRUM, "GMX");
  const stakedGmxTrackerAddressArb = getContract(ARBITRUM, "StakedGmxTracker");

  const { data: arbData, mutate: arbMutate } = useSWR(
    [`StakeV2:stakedGmxSupply:${active}`, ARBITRUM, gmxAddressArb, "balanceOf", stakedGmxTrackerAddressArb],
    {
      fetcher: contractFetcher(library, Token),
    }
  );

  const gmxAddressAvax = getContract(AVALANCHE, "GMX");
  const stakedGmxTrackerAddressAvax = getContract(AVALANCHE, "StakedGmxTracker");

  const { data: avaxData, mutate: avaxMutate } = useSWR(
    [`StakeV2:stakedGmxSupply:${active}`, AVALANCHE, gmxAddressAvax, "balanceOf", stakedGmxTrackerAddressAvax],
    {
      fetcher: contractFetcher(undefined, Token),
    }
  );

  let data;
  if (arbData && avaxData) {
    data = arbData.add(avaxData);
  }

  const mutate = () => {
    arbMutate();
    avaxMutate();
  };

  return { data, mutate };
}

export function useHasOutdatedUi() {
  const url = getServerUrl(ARBITRUM, "/ui_version");
  const { data, mutate } = useSWR([url], {
    fetcher: (...args) => fetch(...args).then((res) => res.text()),
  });

  let hasOutdatedUi = false;

  if (data && parseFloat(data) > parseFloat(UI_VERSION)) {
    hasOutdatedUi = true;
  }

  return { data: hasOutdatedUi, mutate };
}

export function useGmxPrice(chainId, libraries, active) {
  const arbitrumLibrary = libraries && libraries.arbitrum ? libraries.arbitrum : undefined;
  const { data: gmxPriceFromArbitrum, mutate: mutateFromArbitrum } = useGmxPriceFromArbitrum(arbitrumLibrary, active);
  const { data: gmxPriceFromAvalanche, mutate: mutateFromAvalanche } = useGmxPriceFromAvalanche();

  const gmxPrice = chainId === ARBITRUM ? gmxPriceFromArbitrum : gmxPriceFromAvalanche;
  const mutate = useCallback(() => {
    mutateFromAvalanche();
    mutateFromArbitrum();
  }, [mutateFromAvalanche, mutateFromArbitrum]);

  return {
    gmxPrice,
    gmxPriceFromArbitrum,
    gmxPriceFromAvalanche,
    mutate,
  };
}

// use only the supply endpoint on arbitrum, it includes the supply on avalanche
export function useTotalGmxSupply() {
  const gmxSupplyUrlArbitrum = getServerUrl(ARBITRUM, "/gmx_supply");

  const { data: gmxSupply, mutate: updateGmxSupply } = useSWR([gmxSupplyUrlArbitrum], {
    fetcher: (...args) => fetch(...args).then((res) => res.text()),
  });

  return {
    total: gmxSupply ? bigNumberify(gmxSupply) : undefined,
    mutate: updateGmxSupply,
  };
}

export function useTotalGmxStaked() {
  const stakedGmxTrackerAddressArbitrum = getContract(ARBITRUM, "StakedGmxTracker");
  const stakedGmxTrackerAddressAvax = getContract(AVALANCHE, "StakedGmxTracker");
  let totalStakedGmx = useRef(bigNumberify(0));
  const { data: stakedGmxSupplyArbitrum, mutate: updateStakedGmxSupplyArbitrum } = useSWR(
    [
      `StakeV2:stakedGmxSupply:${ARBITRUM}`,
      ARBITRUM,
      getContract(ARBITRUM, "GMX"),
      "balanceOf",
      stakedGmxTrackerAddressArbitrum,
    ],
    {
      fetcher: contractFetcher(undefined, Token),
    }
  );
  const { data: stakedGmxSupplyAvax, mutate: updateStakedGmxSupplyAvax } = useSWR(
    [
      `StakeV2:stakedGmxSupply:${AVALANCHE}`,
      AVALANCHE,
      getContract(AVALANCHE, "GMX"),
      "balanceOf",
      stakedGmxTrackerAddressAvax,
    ],
    {
      fetcher: contractFetcher(undefined, Token),
    }
  );

  const mutate = useCallback(() => {
    updateStakedGmxSupplyArbitrum();
    updateStakedGmxSupplyAvax();
  }, [updateStakedGmxSupplyArbitrum, updateStakedGmxSupplyAvax]);

  if (stakedGmxSupplyArbitrum && stakedGmxSupplyAvax) {
    let total = bigNumberify(stakedGmxSupplyArbitrum).add(stakedGmxSupplyAvax);
    totalStakedGmx.current = total;
  }

  return {
    avax: stakedGmxSupplyAvax,
    arbitrum: stakedGmxSupplyArbitrum,
    total: totalStakedGmx.current,
    mutate,
  };
}

export function useTotalGmxInLiquidity() {
  let poolAddressArbitrum = getContract(ARBITRUM, "UniswapGmxEthPool");
  let poolAddressAvax = getContract(AVALANCHE, "TraderJoeGmxAvaxPool");
  let totalGMX = useRef(bigNumberify(0));

  const { data: gmxInLiquidityOnArbitrum, mutate: mutateGMXInLiquidityOnArbitrum } = useSWR(
    [`StakeV2:gmxInLiquidity:${ARBITRUM}`, ARBITRUM, getContract(ARBITRUM, "GMX"), "balanceOf", poolAddressArbitrum],
    {
      fetcher: contractFetcher(undefined, Token),
    }
  );
  const { data: gmxInLiquidityOnAvax, mutate: mutateGMXInLiquidityOnAvax } = useSWR(
    [`StakeV2:gmxInLiquidity:${AVALANCHE}`, AVALANCHE, getContract(AVALANCHE, "GMX"), "balanceOf", poolAddressAvax],
    {
      fetcher: contractFetcher(undefined, Token),
    }
  );
  const mutate = useCallback(() => {
    mutateGMXInLiquidityOnArbitrum();
    mutateGMXInLiquidityOnAvax();
  }, [mutateGMXInLiquidityOnArbitrum, mutateGMXInLiquidityOnAvax]);

  if (gmxInLiquidityOnAvax && gmxInLiquidityOnArbitrum) {
    let total = bigNumberify(gmxInLiquidityOnArbitrum).add(gmxInLiquidityOnAvax);
    totalGMX.current = total;
  }
  return {
    avax: gmxInLiquidityOnAvax,
    arbitrum: gmxInLiquidityOnArbitrum,
    total: totalGMX.current,
    mutate,
  };
}

function useGmxPriceFromAvalanche() {
  const poolAddress = getContract(AVALANCHE, "TraderJoeGmxAvaxPool");

  const { data, mutate: updateReserves } = useSWR(["TraderJoeGmxAvaxReserves", AVALANCHE, poolAddress, "getReserves"], {
    fetcher: contractFetcher(undefined, UniswapV2),
  });
  const { _reserve0: gmxReserve, _reserve1: avaxReserve } = data || {};

  const vaultAddress = getContract(AVALANCHE, "Vault");
  const avaxAddress = getTokenBySymbol(AVALANCHE, "WAVAX").address;
  const { data: avaxPrice, mutate: updateAvaxPrice } = useSWR(
    [`StakeV2:avaxPrice`, AVALANCHE, vaultAddress, "getMinPrice", avaxAddress],
    {
      fetcher: contractFetcher(undefined, Vault),
    }
  );

  const PRECISION = bigNumberify(10).pow(18);
  let gmxPrice;
  if (avaxReserve && gmxReserve && avaxPrice) {
    gmxPrice = avaxReserve.mul(PRECISION).div(gmxReserve).mul(avaxPrice).div(PRECISION);
  }

  const mutate = useCallback(() => {
    updateReserves(undefined, true);
    updateAvaxPrice(undefined, true);
  }, [updateReserves, updateAvaxPrice]);

  return { data: gmxPrice, mutate };
}

function useGmxPriceFromArbitrum(library, active) {
  const poolAddress = getContract(ARBITRUM, "UniswapGmxEthPool");
  const { data: uniPoolSlot0, mutate: updateUniPoolSlot0 } = useSWR(
    [`StakeV2:uniPoolSlot0:${active}`, ARBITRUM, poolAddress, "slot0"],
    {
      fetcher: contractFetcher(library, UniPool),
    }
  );

  const vaultAddress = getContract(ARBITRUM, "Vault");
  const ethAddress = getTokenBySymbol(ARBITRUM, "WETH").address;
  const { data: ethPrice, mutate: updateEthPrice } = useSWR(
    [`StakeV2:ethPrice:${active}`, ARBITRUM, vaultAddress, "getMinPrice", ethAddress],
    {
      fetcher: contractFetcher(library, Vault),
    }
  );

  const gmxPrice = useMemo(() => {
    if (uniPoolSlot0 && ethPrice) {
      const tokenA = new UniToken(ARBITRUM, ethAddress, 18, "SYMBOL", "NAME");

      const gmxAddress = getContract(ARBITRUM, "GMX");
      const tokenB = new UniToken(ARBITRUM, gmxAddress, 18, "SYMBOL", "NAME");

      const pool = new Pool(
        tokenA, // tokenA
        tokenB, // tokenB
        10000, // fee
        uniPoolSlot0.sqrtPriceX96, // sqrtRatioX96
        1, // liquidity
        uniPoolSlot0.tick, // tickCurrent
        []
      );

      const poolTokenPrice = pool.priceOf(tokenB).toSignificant(6);
      const poolTokenPriceAmount = parseValue(poolTokenPrice, 18);
      return poolTokenPriceAmount.mul(ethPrice).div(expandDecimals(1, 18));
    }
  }, [ethPrice, uniPoolSlot0, ethAddress]);

  const mutate = useCallback(() => {
    updateUniPoolSlot0(undefined, true);
    updateEthPrice(undefined, true);
  }, [updateEthPrice, updateUniPoolSlot0]);

  return { data: gmxPrice, mutate };
}

export async function approvePlugin(
  chainId,
  pluginAddress,
  { library, pendingTxns, setPendingTxns, sentMsg, failMsg }
) {
  const routerAddress = getContract(chainId, "Router");
  const contract = new ethers.Contract(routerAddress, Router.abi, library.getSigner());
  return callContract(chainId, contract, "approvePlugin", [pluginAddress], {
    sentMsg,
    failMsg,
    pendingTxns,
    setPendingTxns,
  });
}

export async function createSwapOrder(
  chainId,
  library,
  path,
  amountIn,
  minOut,
  triggerRatio,
  nativeTokenAddress,
  opts = {}
) {
  const executionFee = getConstant(chainId, "SWAP_ORDER_EXECUTION_GAS_FEE");
  const triggerAboveThreshold = false;
  let shouldWrap = false;
  let shouldUnwrap = false;
  opts.value = executionFee;

  if (path[0] === AddressZero) {
    shouldWrap = true;
    opts.value = opts.value.add(amountIn);
  }
  if (path[path.length - 1] === AddressZero) {
    shouldUnwrap = true;
  }
  path = replaceNativeTokenAddress(path, nativeTokenAddress);

  const params = [path, amountIn, minOut, triggerRatio, triggerAboveThreshold, executionFee, shouldWrap, shouldUnwrap];

  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, library.getSigner());

  return callContract(chainId, contract, "createSwapOrder", params, opts);
}

export async function createIncreaseOrder(
  chainId,
  library,
  nativeTokenAddress,
  path,
  amountIn,
  indexTokenAddress,
  minOut,
  sizeDelta,
  collateralTokenAddress,
  isLong,
  triggerPrice,
  opts = {}
) {
  invariant(!isLong || indexTokenAddress === collateralTokenAddress, "invalid token addresses");
  invariant(indexTokenAddress !== AddressZero, "indexToken is 0");
  invariant(collateralTokenAddress !== AddressZero, "collateralToken is 0");

  const fromETH = path[0] === AddressZero;

  path = replaceNativeTokenAddress(path, nativeTokenAddress);
  const shouldWrap = fromETH;
  const triggerAboveThreshold = !isLong;
  const executionFee = getConstant(chainId, "INCREASE_ORDER_EXECUTION_GAS_FEE");

  const params = [
    path,
    amountIn,
    indexTokenAddress,
    minOut,
    sizeDelta,
    collateralTokenAddress,
    isLong,
    triggerPrice,
    triggerAboveThreshold,
    executionFee,
    shouldWrap,
  ];

  if (!opts.value) {
    opts.value = fromETH ? amountIn.add(executionFee) : executionFee;
  }

  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, library.getSigner());

  return callContract(chainId, contract, "createIncreaseOrder", params, opts);
}

export async function createDecreaseOrder(
  chainId,
  library,
  indexTokenAddress,
  sizeDelta,
  collateralTokenAddress,
  collateralDelta,
  isLong,
  triggerPrice,
  triggerAboveThreshold,
  opts = {}
) {
  invariant(!isLong || indexTokenAddress === collateralTokenAddress, "invalid token addresses");
  invariant(indexTokenAddress !== AddressZero, "indexToken is 0");
  invariant(collateralTokenAddress !== AddressZero, "collateralToken is 0");

  const executionFee = getConstant(chainId, "DECREASE_ORDER_EXECUTION_GAS_FEE");

  const params = [
    indexTokenAddress,
    sizeDelta,
    collateralTokenAddress,
    collateralDelta,
    isLong,
    triggerPrice,
    triggerAboveThreshold,
  ];
  opts.value = executionFee;
  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, library.getSigner());

  return callContract(chainId, contract, "createDecreaseOrder", params, opts);
}

export async function cancelSwapOrder(chainId, library, index, opts) {
  const params = [index];
  const method = "cancelSwapOrder";
  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, library.getSigner());

  return callContract(chainId, contract, method, params, opts);
}

export async function cancelDecreaseOrder(chainId, library, index, opts) {
  const params = [index];
  const method = "cancelDecreaseOrder";
  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, library.getSigner());

  return callContract(chainId, contract, method, params, opts);
}

export async function cancelIncreaseOrder(chainId, library, index, opts) {
  const params = [index];
  const method = "cancelIncreaseOrder";
  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, library.getSigner());

  return callContract(chainId, contract, method, params, opts);
}

export function handleCancelOrder(chainId, library, order, opts) {
  let func;
  if (order.type === SWAP) {
    func = cancelSwapOrder;
  } else if (order.type === INCREASE) {
    func = cancelIncreaseOrder;
  } else if (order.type === DECREASE) {
    func = cancelDecreaseOrder;
  }

  return func(chainId, library, order.index, {
    successMsg: t`Order cancelled.`,
    failMsg: t`Cancel failed.`,
    sentMsg: t`Cancel submitted.`,
    pendingTxns: opts.pendingTxns,
    setPendingTxns: opts.setPendingTxns,
  });
}

export async function cancelMultipleOrders(chainId, library, allIndexes = [], opts) {
  const ordersWithTypes = groupBy(allIndexes, (v) => v.split("-")[0]);
  function getIndexes(key) {
    if (!ordersWithTypes[key]) return;
    return ordersWithTypes[key].map((d) => d.split("-")[1]);
  }
  // params order => swap, increase, decrease
  const params = ["Swap", "Increase", "Decrease"].map((key) => getIndexes(key) || []);
  const method = "cancelMultiple";
  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, library.getSigner());
  return callContract(chainId, contract, method, params, opts);
}

export async function updateDecreaseOrder(
  chainId,
  library,
  index,
  collateralDelta,
  sizeDelta,
  triggerPrice,
  triggerAboveThreshold,
  opts
) {
  const params = [index, collateralDelta, sizeDelta, triggerPrice, triggerAboveThreshold];
  const method = "updateDecreaseOrder";
  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, library.getSigner());

  return callContract(chainId, contract, method, params, opts);
}

export async function updateIncreaseOrder(
  chainId,
  library,
  index,
  sizeDelta,
  triggerPrice,
  triggerAboveThreshold,
  opts
) {
  const params = [index, sizeDelta, triggerPrice, triggerAboveThreshold];
  const method = "updateIncreaseOrder";
  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, library.getSigner());

  return callContract(chainId, contract, method, params, opts);
}

export async function updateSwapOrder(chainId, library, index, minOut, triggerRatio, triggerAboveThreshold, opts) {
  const params = [index, minOut, triggerRatio, triggerAboveThreshold];
  const method = "updateSwapOrder";
  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, library.getSigner());

  return callContract(chainId, contract, method, params, opts);
}

export async function _executeOrder(chainId, library, method, account, index, feeReceiver, opts) {
  const params = [account, index, feeReceiver];
  const positionManagerAddress = getContract(chainId, "PositionManager");
  const contract = new ethers.Contract(positionManagerAddress, PositionManager.abi, library.getSigner());
  return callContract(chainId, contract, method, params, opts);
}

export function executeSwapOrder(chainId, library, account, index, feeReceiver, opts) {
  return _executeOrder(chainId, library, "executeSwapOrder", account, index, feeReceiver, opts);
}

export function executeIncreaseOrder(chainId, library, account, index, feeReceiver, opts) {
  return _executeOrder(chainId, library, "executeIncreaseOrder", account, index, feeReceiver, opts);
}

export function executeDecreaseOrder(chainId, library, account, index, feeReceiver, opts) {
  return _executeOrder(chainId, library, "executeDecreaseOrder", account, index, feeReceiver, opts);
}
